// لوحة مسار: رقم واحد واضح لكل بطاقة (نمط مركز القيادة) + الأساسي/التطويري + التوزيع + الاحتياج + أعلى الدورات
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminUser } from "@/v2/lib/rbac";
import { GlassCard, Chip, Skeleton } from "@/v2/components/ui";
import { PageHead } from "@/v2/admin/ui";
import { useDashboard, fmtInd, fmtNum } from "@/masar/lib";
import {
  LayoutDashboard, Users, Route, CheckCircle2, Target, AlertTriangle, Award, ShieldCheck, Tags, Info, ChevronLeft,
  Building2, GraduationCap, FileText, RefreshCw, ExternalLink, Layers, PlayCircle, Flag, TrendingUp, X,
} from "lucide-react";

const TONES = {
  emerald: ["#006654", "rgba(0,102,84,0.10)"], gold: ["#bb9661", "rgba(187,150,97,0.14)"],
  red: ["#a83e1e", "rgba(168,62,30,0.10)"], muted: ["#66717a", "#e3e7ea"], sky: ["#4f7a99", "rgba(120,170,220,0.14)"],
};

/** بطاقة مؤشر: أيقونة، رقم رئيسي واحد، عنوان، سطر توضيحي صغير، شرح عند الطلب، وانتقال للتفاصيل */
function Stat({ icon: I, ind, sub, tone = "emerald", to, testId, onClick, delay = 0 }) {
  const navigate = useNavigate();
  const [help, setHelp] = useState(false);
  const [c] = TONES[tone] || TONES.emerald;
  const na = ind?.status === "na" || ind?.status === "nc";
  const go = onClick || (to ? () => navigate(to) : null);
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }} className="h-full">
      <GlassCard hover={!!go} className="p-4 sm:p-5 h-full flex flex-col relative" data-testid={testId}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 text-sm text-ink-3 pt-1"><I size={15} strokeWidth={1.8} style={{ color: c }} aria-hidden="true" />{ind?.label}</div>
          <button onClick={(e) => { e.stopPropagation(); setHelp((v) => !v); }} title="ما معنى هذا المؤشر؟" aria-label="ما معنى هذا المؤشر؟" data-testid={testId ? `${testId}-help` : undefined}
            className="w-7 h-7 rounded-full text-ink-2 hover:text-ink hover:bg-surface-3 flex items-center justify-center">{help ? <X size={13} /> : <Info size={13} />}</button>
        </div>
        <button onClick={go || undefined} disabled={!go} className="text-start mt-3 flex-1 disabled:cursor-default" data-testid={testId ? `${testId}-open` : undefined}>
          {na ? (
            <div className="text-sm font-bold leading-snug text-beige-ink" dir="rtl">{ind.status === "na" ? "غير متاح" : "لا نشاط مسجل بعد"}</div>
          ) : (
            <div className="text-[1.9rem] leading-tight font-semibold tabular-nums" dir="ltr" style={{ textAlign: "right" }}>{fmtInd(ind)}</div>
          )}
          {na ? <div className="text-xs text-ink-3 mt-1 leading-relaxed">{ind.reason}</div>
            : sub ? <div className="text-xs text-ink-3 mt-1 leading-relaxed" dir="rtl">{sub}</div> : null}
        </button>
        {help && <div className="mt-2 text-xs leading-relaxed text-ink-2 border-t border-line pt-2" data-testid={testId ? `${testId}-definition` : undefined}>{ind?.definition || "—"}</div>}
      </GlassCard>
    </motion.div>
  );
}

function Distribution({ rows, total }) {
  const { user } = useAuth();
  const rootLabel = user?.supervision ? "الجهات ضمن نطاقك" : "القطاعات";
  const [stack, setStack] = useState([]);
  const parent = stack[stack.length - 1];
  const { data: child, isLoading } = useQuery({
    queryKey: ["masar-dist", parent?.unit_id], enabled: !!parent,
    queryFn: async () => (await api.get(`/masar/admin/dashboard/distribution?parent_id=${parent.unit_id}`)).data,
  });
  const list = parent ? child?.rows || [] : rows;
  const max = Math.max(1, ...list.map((r) => r.count));
  return (
    <GlassCard className="p-5" data-testid="dist-card">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h3 className="font-bold flex items-center gap-2"><Building2 size={16} className="text-beige-ink" /> توزيع الموظفين {parent ? `داخل «${parent.name}»` : `حسب ${rootLabel}`}</h3>
        <div className="text-xs text-ink-2">مجموع المستوى = <b className="text-ink" dir="ltr">{fmtNum(total)}</b> موظفًا</div>
      </div>
      {stack.length > 0 && (
        <div className="flex items-center gap-1 text-xs text-ink-2 mb-3 flex-wrap">
          <button onClick={() => setStack([])} className="underline hover:text-ink" data-testid="dist-root">{rootLabel}</button>
          {stack.map((s, i) => <span key={s.unit_id} className="flex items-center gap-1"><ChevronLeft size={12} /><button onClick={() => setStack(stack.slice(0, i + 1))} className="hover:text-ink">{s.name}</button></span>)}
        </div>
      )}
      {isLoading ? <Skeleton className="h-24 w-full" /> : list.length === 0 ? <p className="text-sm text-ink-2">لا توزيع فرعي لهذه الجهة.</p> : (
        <div className="space-y-2" data-testid="dist-rows">
          {list.map((r) => (
            <button key={r.name} disabled={!r.unit_id} onClick={() => r.unit_id && setStack([...stack, r])} data-testid="dist-row"
              className="w-full text-start group disabled:cursor-default">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className={`truncate ${r.unit_id ? "group-hover:text-beige-ink" : ""}`}>{r.name} {r.unit_id && <ChevronLeft size={11} className="inline opacity-60" />}</span>
                <span className="shrink-0 tabular-nums" dir="ltr"><b className="text-ink">{fmtNum(r.count)}</b> <span className="text-ink-2">({r.pct ?? 0}%)</span></span>
              </div>
              <div className="h-2 rounded-full bg-surface-3 overflow-hidden"><div className="h-full bg-beige" style={{ width: `${(r.count / max) * 100}%` }} /></div>
            </button>
          ))}
        </div>
      )}
    </GlassCard>
  );
}

function Bars({ title, icon: I, rows, labelKey, valueKey, empty, testId, valueLabel }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey]));
  return (
    <GlassCard className="p-5" data-testid={testId}>
      <h3 className="font-bold mb-3 flex items-center gap-2"><I size={16} className="text-beige-ink" /> {title}</h3>
      {rows.length === 0 ? <p className="text-sm text-ink-2">{empty}</p> : (
        <div className="space-y-2.5">
          {rows.map((r, i) => (
            <div key={`${r[labelKey]}-${i}`} className="flex items-center gap-3 text-xs" data-testid={`${testId}-row`}>
              <span className="w-5 text-ink-2 tabular-nums" dir="ltr">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1"><span className="truncate">{r[labelKey]}</span><b className="text-ink tabular-nums shrink-0" dir="ltr">{fmtNum(r[valueKey])}</b></div>
                <div className="h-2 rounded-full bg-surface-3 overflow-hidden"><div className="h-full bg-beige" style={{ width: `${(r[valueKey] / max) * 100}%` }} /></div>
              </div>
            </div>
          ))}
          {valueLabel && <div className="text-xs text-ink-2 pt-1">{valueLabel}</div>}
        </div>
      )}
    </GlassCard>
  );
}

const MG_ICONS = { basic_completion_pct: Flag, basic_enrolled: Users, basic_start_pct: PlayCircle, basic_started: PlayCircle, dev_achievers: TrendingUp, dev_transition_pct: Layers };

export default function MasarOverview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [asOf, setAsOf] = useState("");
  const { data, isLoading, isError, refetch, isFetching } = useDashboard(asOf ? { as_of: asOf } : {});
  if (isLoading) return <div className="space-y-4"><Skeleton className="h-16 w-full" /><div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-36 w-full" />)}</div></div>;
  if (isError || !data) {
    return (
      <GlassCard className="p-10 text-center" data-testid="dashboard-error">
        <AlertTriangle className="mx-auto mb-3 text-danger" />
        <div className="font-bold mb-1">تعذّر تحميل بيانات اللوحة</div>
        <p className="text-sm text-ink-2 mb-4">لم تُحمَّل الأرقام — هذه حالة تعذّر وليست صفرًا.</p>
        <button onClick={() => refetch()} className="v2-btn" data-testid="dashboard-retry"><RefreshCw size={15} /> إعادة المحاولة</button>
      </GlassCard>
    );
  }
  const c = data.cards, mg = data.management, q = data.queues || {};
  const ful = c.fulfilment;
  const top10 = mg.top_dev_programs?.rows || [];
  const canClassify = isAdminUser(user);
  const certificatesRoute = isAdminUser(user) ? "/masar/admin/certificates" : undefined;

  return (
    <div className="space-y-6" data-testid="masar-overview">
      <PageHead icon={LayoutDashboard} title="لوحة مسار" sub={data.scope === "limited" ? "الأرقام محصورة بنطاقك التنظيمي الممنوح." : "أرقام حقيقية مجمّعة على مستوى المنصة — المصدر نفسه المستخدم في التقارير."}>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="v2-inset px-3 py-1.5 flex items-center gap-2 text-xs">
            <span className="text-ink-2">حالة حتى تاريخ</span>
            <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="bg-transparent outline-none text-ink text-xs" data-testid="dashboard-as-of" dir="ltr" />
            {asOf && <button onClick={() => setAsOf("")} className="text-ink-2 hover:text-ink" aria-label="إلغاء التاريخ"><X size={12} /></button>}
          </div>
          <button onClick={() => refetch()} className="v2-btn-ghost v2-btn-sm" data-testid="dashboard-refresh"><RefreshCw size={13} className={isFetching ? "animate-spin" : ""} /> تحديث</button>
        </div>
      </PageHead>
      <div className="text-xs text-ink-2 -mt-3 flex flex-wrap gap-x-3 gap-y-1" data-testid="dashboard-period">
        <span>{(data.period_desc || []).join(" · ")}</span>
        <span>· آخر تحديث <span dir="ltr">{String(data.generated_at).slice(0, 16).replace("T", " ")}</span> UTC</span>
        {asOf && <span className="text-beige-ink">· تغيير التاريخ يعرض لقطة الحالة حتى ذلك اليوم (لا حركة الفترة)</span>}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-cards">
        <Stat icon={Users} ind={c.registered} sub={`${fmtNum(data.assigned)} مرتبطون بملف وظيفي · ${fmtNum(data.unassigned)} بلا ملف`} to="/masar/admin/users" testId="card-registered" />
        <Stat icon={Route} ind={c.active_plans} tone="gold" sub={`${fmtNum(c.active_plans.breakdown?.not_started)} لم تبدأ · ${fmtNum(c.active_plans.breakdown?.in_progress)} قيد التنفيذ · ${fmtNum(c.active_plans.breakdown?.waiting_program)} تنتظر برنامجًا`} to="/masar/admin/users" testId="card-active-plans" delay={0.04} />
        <Stat icon={CheckCircle2} ind={c.completed_plans} sub="خطط استُوفيت متطلباتها وأُغلقت — لا تشمل من لا يحتاج خطة" to="/masar/admin/users" testId="card-completed-plans" delay={0.08} />
        <Stat icon={Target} ind={ful} tone="sky" sub={ful.denominator ? `${fmtNum(ful.numerator)} من ${fmtNum(ful.denominator)} متطلبًا اكتمل تدريبه · ${fmtNum(ful.unmeasured)} بانتظار دورات` : null} to="/masar/admin/reports" testId="card-fulfilment" delay={0.12} />
        <Stat icon={AlertTriangle} ind={c.gaps} tone="red" sub={`بحسب المصفوفة · ${fmtNum(c.gaps.unmeasured)} متطلبًا بانتظار دورات`} to="/masar/admin/reports" testId="card-gaps" delay={0.16} />
        <Stat icon={Award} ind={c.certs_waiting} tone="gold" sub="طلبات مقدمة تحتاج قرار مراجعة" to={certificatesRoute} testId="card-certs-waiting" delay={0.2} />
        <Stat icon={ShieldCheck} ind={c.certs_valid} sub="اعتماد فعال وسريان — إعادة الرفع لا تضاعف الشهادة" to={certificatesRoute} testId="card-certs-valid" delay={0.24} />
        <Stat icon={Tags} ind={c.offers_open} tone="muted" sub="عروض منشورة وسارية لاحتياجات موظفي النطاق" to={certificatesRoute} testId="card-offers" delay={0.28} />
      </div>

      <GlassCard className="p-5" data-testid="management-section">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
          <h2 className="text-base md:text-lg font-bold flex items-center gap-2"><GraduationCap size={18} className="text-beige-ink" /> برامج المسار — مؤشرات الإدارة</h2>
          {mg.classified ? <Chip tone="emerald">{fmtNum(mg.programs_classified)} برنامجًا منشورًا{mg.programs_unclassified ? ` · ${fmtNum(mg.programs_unclassified)} منشور بلا تصنيف` : ""}</Chip> : <Chip tone="gold">التصنيف غير مكتمل</Chip>}
        </div>
        <p className="text-xs text-ink-2 mb-4">تُحسب المؤشرات من البرامج المسندة لموظفي النطاق وسجلات البدء والإتمام الفعلية.</p>
        {!mg.classified && (
          <div className="v2-inset p-4 mb-4 flex items-center gap-3 flex-wrap" data-testid="management-na">
            <AlertTriangle size={18} className="text-beige-ink shrink-0" />
            <div className="flex-1 min-w-[220px] text-sm"><b>لم تُنشر دورات في هذا النطاق بعد.</b><div className="text-xs text-ink-2 mt-0.5">لم يُصنَّف أي برنامج منشور كأساسي أو تطويري بعد؛ تُحسب المؤشرات 3–9 تلقائيًا فور التصنيف في تميز. المؤشران 1 و2 متاحان.</div></div>
            {canClassify && <button onClick={() => navigate("/admin/content/challenges")} className="v2-btn v2-btn-sm" data-testid="management-classify"><ExternalLink size={13} /> استكمال التصنيف في تميز</button>}
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={Users} ind={mg.registered} tone="muted" testId="mg-registered" />
          {["basic_completion_pct", "basic_enrolled", "basic_start_pct", "basic_started", "dev_achievers", "dev_transition_pct"].map((k, i) => (
            <Stat key={k} icon={MG_ICONS[k]} ind={mg[k]} tone={k.startsWith("dev") ? "sky" : "gold"} testId={`mg-${k}`} delay={i * 0.03}
              sub={mg[k]?.denominator != null && mg[k].status === "ok" ? `${fmtNum(mg[k].numerator)} من ${fmtNum(mg[k].denominator)}` : null} />
          ))}
        </div>
        <div className="mt-4">
          <Bars title="9) أعلى عشر دورات تسجيلًا في تميز" icon={PlayCircle} rows={top10} labelKey="name" valueKey="count" testId="top-dev-programs"
            empty={mg.top_dev_programs?.status === "na" ? "غير متاح — يلزم تصنيف البرامج التطويرية في تميز." : "لا التحاقات ببرامج المسار بعد."}
            valueLabel="العدد = موظفون فريدون لكل برنامج؛ إعادة التسجيل أو المحاولة لا تُضاعف. الإدارة والتصنيف في تميز فقط." />
        </div>
      </GlassCard>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <Distribution rows={data.distribution_sectors || []} total={c.registered.value} />
          <GlassCard className="p-4 flex items-center gap-4 flex-wrap text-xs" data-testid="tracks-covered">
            <Route size={16} className="text-ok" />
            <span>المسارات الوظيفية المشمولة: <b className="text-ink" dir="ltr">{fmtNum(data.tracks_covered)}</b> <span className="text-ink-2">(عدد معرفات المسارات الفعلية — لا عدد الخطط ولا الموظفين)</span></span>
          </GlassCard>
        </div>
        <Bars title="أكثر الجدارات ذات متطلبات تدريب متبقية" icon={Target} rows={(data.top_needs || [])} labelKey="label" valueKey="count" testId="top-needs" empty="لا متطلبات تدريب متبقية حاليًا." />
      </div>

      <div className="grid sm:grid-cols-3 gap-3" data-testid="quick-links">
        {[
          { to: "/masar/admin/reports", icon: FileText, t: "مركز التقارير", s: "تقرير شامل أو مخصص بصيغة PDF", id: "ql-reports" },
          { to: "/masar/admin/certificates", icon: Award, t: "طلبات الشهادات", s: `${fmtNum(q.certs_waiting)} بانتظار قرار`, id: "ql-certs" },
          { to: "/masar/admin/certificates", icon: Tags, t: "العروض التدريبية", s: `${fmtNum(c.offers_open.value)} عرضًا ساريًا`, id: "ql-offers" },
        ].filter((l) => l.to !== "/masar/admin/certificates" || certificatesRoute).map((l) => (
          <button key={l.id} onClick={() => navigate(l.to)} className="text-start" data-testid={l.id}>
            <GlassCard hover className="p-4 flex items-center gap-3"><l.icon className="text-beige-ink" size={20} /><div className="flex-1"><div className="font-bold text-sm">{l.t}</div><div className="text-xs text-ink-2">{l.s}</div></div><ChevronLeft size={16} className="text-ink-2" /></GlassCard>
          </button>
        ))}
      </div>
    </div>
  );
}
